import { EmptyState, FlexColumn, FlexRow, Loading, MaterialIcon, useLoading } from '@gorila-shared-ui/components';
import { Block } from 'baseui/block';
import { Button } from 'baseui/button';
import { Pagination } from 'baseui/pagination';
import { HeadingSmall } from 'baseui/typography';
import { useEffect, useState } from 'react';
import AutoSizer from 'react-virtualized-auto-sizer';
import { useRecoilValue, useResetRecoilState } from 'recoil';
import useEffectOnce from '../../hooks/useEffectOnce';
import { useStyles } from '../../hooks/useStyles';
import useUpdateEffect from '../../hooks/useUpdateEffect';
import { getConvoyEvaluations } from '../../services/convoyService';
import { apiParamsState } from '../../storage/app';
import { selectedConvoyIdState, selectedConvoyState, showConvoyEvaluationsState } from '../../storage/convoys';
import { Evaluation } from '../../types/evaluations';
import { EvaluationCard } from '../evaluations/EvaluationCard';
import { FloatingCardContent } from '../layout/cards/FloatingCardContent';
import { StyledTooltip } from '../ui/StyledTooltip';

export function ConvoyEvaluations() {
  const { theme, css, classes } = useStyles();
  const { loading, startLoading, stopLoading } = useLoading(true);
  const [evaluations, setEvaluations] = useState<Evaluation[]>();
  const [page, setPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [reloadPage, setReloadPage] = useState(false);
  const selectedConvoyId = useRecoilValue(selectedConvoyIdState);
  const resetSelectedConvoyId = useResetRecoilState(selectedConvoyIdState);
  const resetShowConvoyEvaluations = useResetRecoilState(showConvoyEvaluationsState);
  const convoy = useRecoilValue(selectedConvoyState);
  const apiParams = useRecoilValue(apiParamsState);
  const itemsPerPage = 50;

  useEffectOnce(() => {
    return () => {
      onClose();
    };
  });

  useUpdateEffect(() => {
    onClose();
  }, [apiParams?.clientId, apiParams?.subClientId]);

  useUpdateEffect(() => {
    setPage(0);
  }, [reloadPage]);

  useUpdateEffect(() => {
    setPage(0);
  }, [selectedConvoyId]);

  useEffect(() => {
    if (!apiParams || !selectedConvoyId) return;

    if (page === 0) {
      setPage(1);
      return;
    }

    const loadEvaluations = async () => {
      startLoading();
      const { error, total, evaluations } = await getConvoyEvaluations(
        apiParams.clientId,
        apiParams.subClientId,
        selectedConvoyId,
        page
      );
      if (!error && evaluations && total !== undefined) {
        setEvaluations(evaluations);
        setTotalItems(total);
      } else {
        setEvaluations(undefined);
        setTotalItems(0);
      }
      stopLoading();
    };

    loadEvaluations();
  }, [page]);

  const onClose = () => {
    resetSelectedConvoyId();
    resetShowConvoyEvaluations();
  };

  const handlePageChange = (nextPage: number) => {
    if (nextPage < 1) {
      return;
    }
    if (nextPage > Math.ceil(totalItems / itemsPerPage)) {
      return;
    }
    setPage(nextPage);
  };

  if (!convoy) return null;

  return (
    <FloatingCardContent
      flexible
      gap={theme.sizing.scale600}
    >
      <FlexRow classNames={css({ alignItems: 'center', justifyContent: 'space-between' })}>
        <HeadingSmall margin={0}>{`${convoy.name} - Evaluaciones`}</HeadingSmall>
        <FlexRow gap={theme.sizing.scale200}>
          <StyledTooltip
            content={'Recargar evaluaciones'}
            showArrow={false}
          >
            <Button
              size="compact"
              shape="square"
              onClick={() => setReloadPage((prev) => !prev)}
              kind="tertiary"
            >
              <MaterialIcon
                name={'refresh'}
                size="mini"
              />
            </Button>
          </StyledTooltip>
          <StyledTooltip
            content="Cerrar"
            showArrow={false}
          >
            <Button
              shape="circle"
              kind="tertiary"
              size="compact"
              onClick={onClose}
            >
              <MaterialIcon
                name="close"
                size="mini"
              />
            </Button>
          </StyledTooltip>
        </FlexRow>
      </FlexRow>
      <div style={{ flex: 1 }}>
        <AutoSizer>
          {({ width, height }) => (
            <FlexColumn
              classNames={css({
                width: `${width}px`,
                height: `${height}px`,
                overflowY: 'auto',
              })}
            >
              {!evaluations && !loading && <EmptyState title="No se pudieron cargar las evaluaciones" />}
              {!evaluations && loading && <Loading />}
              {evaluations && loading && <Loading />}
              {evaluations && !loading && (
                <>
                  {!evaluations.length && <EmptyState title="No existen evaluaciones" />}
                  {evaluations.map((evaluation, evaluationIndex) => (
                    <EvaluationCard
                      evaluation={evaluation}
                      key={evaluationIndex}
                    />
                  ))}
                </>
              )}
            </FlexColumn>
          )}
        </AutoSizer>
      </div>
      {evaluations?.length ? (
        <FlexRow classNames={classes.centeredEnd}>
          <Pagination
            labels={{
              nextButton: 'Siguiente',
              preposition: 'de',
              prevButton: 'Anterior',
            }}
            size="compact"
            currentPage={page > 0 ? page : 1}
            numPages={Math.ceil(totalItems / itemsPerPage)}
            onPageChange={({ nextPage }) => handlePageChange(nextPage)}
          />
        </FlexRow>
      ) : (
        <Block height="44px" />
      )}
    </FloatingCardContent>
  );
}
